/**
 * Reading an SVG into editable Maipo artwork.
 *
 * The point is not merely to open the file. A generated SVG arrives as flat
 * paths carrying repeated hex literals — the reference file is 270 paths with 23
 * distinct fills, `#EE4646` alone on 90 of them — so opening it verbatim would
 * reproduce exactly the problem this editor exists to solve: to change one
 * colour you would select ninety objects.
 *
 * So identical fills are deduped into GLOBAL SWATCHES on the way in, and every
 * path references one. The imported document then behaves like a Maipo trace:
 * one swatch edit recolours all ninety regions in a single undo step, while each
 * path stays its own selectable object.
 */

import { IDENTITY_MATRIX, invertMatrix, multiplyMatrix, transformPoint, type Matrix } from '../core/geometry';
import { createGlobalSwatch, GLOBAL_SWATCHES_EXTENSION, type GlobalSwatch } from '../core/global-swatches';
import { createDocument, createGroupLayer, createId, createLayerBase, type EditorDocument, type ImportNotice, type Layer } from '../core/model';
import { migratePathCommands } from '../vector/migration';
import type { EllipseObject, PathObject, RectangleObject, VectorObject } from '../vector/types';
import { inlinePaintAttributes, parseColour, parseColourAlpha, parseLength, parsePathData, parsePoints, parseTransformList, scanSvg, SvgParseError, SVG_LIMITS, type SvgElement } from './svg/parse';
import { throwIfAborted, type CodecContext, type CodecReadResult, type DocumentCodec } from './types';

/** A clip-path reference, in the user space of the element that declared it. */
interface ClipReference {
  readonly id: string;
  readonly transform: Matrix;
}

interface InheritedState {
  readonly transform: Matrix;
  readonly children: Layer[];
  readonly fill: string | null;
  /** Alpha carried by the fill colour itself (rgba(), #rrggbbaa). */
  readonly fillAlpha: number;
  readonly stroke: string;
  readonly fillOpacity: number;
  readonly fillRule: 'nonzero' | 'evenodd';
  readonly opacity: number;
  readonly display: string;
  readonly displayed: boolean;
  readonly visibility: 'visible' | 'hidden' | 'collapse';
  /** Every clip applying to this element: its ancestors' and its own. */
  readonly clips: readonly ClipReference[];
  /** Raw gradient fill, inherited so a wrapping <g> applies. */
  readonly gradientFill: string | undefined;
}

interface ClipRect { readonly x: number; readonly y: number; readonly width: number; readonly height: number; readonly transform: Matrix }
interface ClipDefinition {
  readonly rects: ClipRect[];
  /** False once the clip contains anything but plain rectangles. */
  exact: boolean;
  readonly objectBoundingBox: boolean;
  readonly transform: Matrix;
}
interface GradientDefinition { readonly stops: string[]; readonly href: string | undefined }
interface Definitions {
  readonly gradients: ReadonlyMap<string, GradientDefinition>;
  readonly clips: ReadonlyMap<string, ClipDefinition>;
}

const CLIP_VISIBILITY_ERROR = 'This SVG controls visibility inside a clip path, which this reader does not support. Flatten the clip and try again.';

/**
 * Gathers gradients and clip paths before any artwork is read. Design tools
 * disagree on where definitions go: Figma, for one, writes `<defs>` after the
 * artwork that references them. A single forward pass would then treat every
 * reference as missing, refusing a no-op clip or reporting a gradient under
 * the wrong colour.
 */
function collectDefinitions(text: string, signal?: AbortSignal): Definitions {
  const gradients = new Map<string, GradientDefinition>();
  const clips = new Map<string, ClipDefinition>();
  let currentGradient: string | null = null;
  // Anonymous clip paths still have contents that must not count as artwork.
  let currentClip: ClipDefinition | null = null;
  let clipDepth = 0;
  scanSvg(text, (element) => {
    throwIfAborted(signal);
    const attributes = inlinePaintAttributes(element.attributes);
    if ((element.name === 'clippath' || currentClip) && (attributes.has('display') || attributes.has('visibility'))) {
      throw new SvgParseError(CLIP_VISIBILITY_ERROR);
    }
    if (element.name === 'clippath') {
      const transform = attributes.get('transform');
      const definition: ClipDefinition = {
        rects: [],
        exact: true,
        objectBoundingBox: attributes.get('clippathunits')?.trim() === 'objectBoundingBox',
        transform: transform ? parseTransformList(transform) : IDENTITY_MATRIX,
      };
      const id = attributes.get('id');
      if (id) clips.set(id, definition);
      if (!element.selfClosing) { currentClip = definition; clipDepth = element.depth; }
      return;
    }
    if (element.name === 'lineargradient' || element.name === 'radialgradient') {
      const id = attributes.get('id');
      if (id) gradients.set(id, { stops: [], href: attributes.get('href') });
      if (!element.selfClosing) currentGradient = id ?? null;
      return;
    }
    if (element.name === 'stop') {
      const stop = attributes.get('stop-color');
      const colour = stop && currentGradient ? parseColour(stop) : null;
      if (colour && currentGradient) gradients.get(currentGradient)?.stops.push(colour);
      return;
    }
    if (!currentClip) return;
    // Only direct rectangles keep the no-op test exact; groups, paths,
    // circles or text inside a clip make it a clip this reader cannot prove.
    // scanSvg counts an open tag in its own depth; a self-closing one is not.
    const parentDepth = element.selfClosing ? element.depth : element.depth - 1;
    if (element.name !== 'rect' || parentDepth !== clipDepth) { currentClip.exact = false; return; }
    const width = parseLength(attributes.get('width'), 'width');
    const height = parseLength(attributes.get('height'), 'height');
    if (width <= 0 || height <= 0) return;
    // A rounded clip rectangle bites its corners. It still wholly contains two
    // bands the rounding cannot reach: full height between the horizontal
    // radii, and full width between the vertical ones. Each axis clamps to its
    // own half-extent, as SVG does, so a pill clip keeps its long band.
    const rx = Math.max(0, parseLength(attributes.get('rx'), 'rx', attributes.has('ry') ? parseLength(attributes.get('ry'), 'ry') : 0));
    const ry = Math.max(0, parseLength(attributes.get('ry'), 'ry', attributes.has('rx') ? rx : 0));
    const insetX = Math.min(rx, width / 2);
    const insetY = Math.min(ry, height / 2);
    const x = parseLength(attributes.get('x'), 'x');
    const y = parseLength(attributes.get('y'), 'y');
    const transform = attributes.get('transform');
    const matrix = transform ? parseTransformList(transform) : IDENTITY_MATRIX;
    currentClip.rects.push(
      { x: x + insetX, y, width: width - insetX * 2, height, transform: matrix },
      { x, y: y + insetY, width, height: height - insetY * 2, transform: matrix },
    );
  }, (name) => {
    if (name === 'clippath') currentClip = null;
    if (name === 'lineargradient' || name === 'radialgradient') currentGradient = null;
  });
  return { gradients, clips };
}

/** Stops of a gradient, following plain `href` inheritance as SVG does. */
function gradientStops(definitions: Definitions, id: string): string[] {
  const seen = new Set<string>();
  let current = definitions.gradients.get(id);
  while (current && current.stops.length === 0 && current.href && !seen.has(current.href)) {
    seen.add(current.href);
    const target = /^#(.+)$/.exec(current.href.trim())?.[1];
    current = target ? definitions.gradients.get(target) : undefined;
  }
  return current?.stops ?? [];
}

/** `clip-path` accepts `none` or a URL reference; anything else is refused. */
function clipPathReference(value: string | undefined): string | null {
  if (value === undefined) return null;
  const text = value.trim();
  if (text === '' || text.toLowerCase() === 'none') return null;
  const match = /^url\(\s*(['"]?)#([^'")]+)\1\s*\)$/i.exec(text);
  if (!match) throw new SvgParseError('This SVG uses a clip-path shape this reader does not support. Flatten the clip and try again.');
  return match[2]!;
}

/**
 * True when a shape lies wholly inside one rectangle of the clip, compared in
 * document space so transforms on the shape, its ancestors, the clipPath and
 * the clip rectangles all count. Containment in the union's bounding box is
 * not enough: two separated rectangles would pass artwork between them.
 */
function clipContains(clip: ClipDefinition, reference: ClipReference, shape: ShapeGeometry, shapeTransform: Matrix): boolean {
  const bounds = geometryBounds(shape);
  if (!bounds) return true;
  const corners = [
    { x: bounds.minX, y: bounds.minY }, { x: bounds.maxX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.maxY }, { x: bounds.minX, y: bounds.maxY },
  ].map((point) => transformPoint(shapeTransform, point));
  return clip.rects.some((rect) => {
    const inverse = invertMatrix(multiplyMatrix(multiplyMatrix(reference.transform, clip.transform), rect.transform));
    if (!inverse) return false;
    return corners.every((corner) => {
      const local = transformPoint(inverse, corner);
      return local.x >= rect.x - 0.001 && local.y >= rect.y - 0.001
        && local.x <= rect.x + rect.width + 0.001 && local.y <= rect.y + rect.height + 0.001;
    });
  });
}

function paintOpacity(value: string | undefined, parent: number, inherited: boolean): number {
  const text = value?.trim().toLowerCase();
  if (text === 'inherit' || ((text === undefined || text === 'unset') && inherited)) return parent;
  if (text === undefined || text === 'initial' || text === 'unset') return 1;
  const match = /^([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)(%)?$/.exec(text);
  const number = match ? Number(match[1]) / (match[2] ? 100 : 1) : NaN;
  if (!Number.isFinite(number)) throw new SvgParseError('This SVG has an unsupported opacity value. Use a number or percentage.');
  return Math.max(0, Math.min(1, number));
}

function dimension(value: string | undefined, fallback: number): number {
  if (value === undefined) return fallback;
  const number = parseLength(value, 'viewport size');
  if (!Number.isFinite(number) || number <= 0) {
    throw new SvgParseError(`This SVG's size “${value}” is not a plain pixel measurement. Percentages and physical units are not supported.`);
  }
  return Math.ceil(number);
}

/** SVG 2 viewport mapping for all meet alignments and nonuniform scaling. */
function viewportMatrix(viewBox: string | undefined, width: number, height: number, preserve: string | undefined): Matrix {
  if (!viewBox) return IDENTITY_MATRIX;
  const parts = viewBox.split(/[\s,]+/).filter(Boolean).map(Number);
  if (parts.length !== 4 || parts.some((value) => !Number.isFinite(value))) throw new SvgParseError('This SVG has a viewBox that is not four numbers.');
  const [minX, minY, boxWidth, boxHeight] = parts as [number, number, number, number];
  if (boxWidth <= 0 || boxHeight <= 0) throw new SvgParseError('This SVG has a viewBox with no area.');
  const alignment = (preserve ?? 'xmidymid meet').trim().toLowerCase();
  const parsedAlignment = /^(none|x(min|mid|max)y(min|mid|max))(?:\s+(meet|slice))?$/.exec(alignment);
  if (!parsedAlignment) throw new SvgParseError('This SVG has an invalid preserveAspectRatio alignment.');
  if (parsedAlignment[4] === 'slice') throw new SvgParseError('This SVG uses preserveAspectRatio="… slice", which this reader does not support.');
  const scaleX = width / boxWidth;
  const scaleY = height / boxHeight;
  const uniform = parsedAlignment[1] === 'none' ? null : Math.min(scaleX, scaleY);
  const sx = uniform ?? scaleX;
  const sy = uniform ?? scaleY;
  const factor = (axis: string | undefined) => axis === 'max' ? 1 : axis === 'mid' ? 0.5 : 0;
  const tx = (width - boxWidth * sx) * factor(parsedAlignment[2]) - minX * sx;
  const ty = (height - boxHeight * sy) * factor(parsedAlignment[3]) - minY * sy;
  return [sx, 0, 0, sy, tx, ty];
}

interface Bounds { minX: number; minY: number; maxX: number; maxY: number }

/** Anchor-and-handle extent, which is enough to decide whether a clip bites. */
function geometryBounds(shape: ShapeGeometry): Bounds | null {
  if (shape.kind !== 'path') {
    return {
      minX: shape.geometry.x, minY: shape.geometry.y,
      maxX: shape.geometry.x + shape.geometry.width, maxY: shape.geometry.y + shape.geometry.height,
    };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of shape.geometry.nodes) {
    for (const point of [node.anchor, node.incoming, node.outgoing]) {
      if (!point) continue;
      if (point.x < minX) minX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
    }
  }
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}

/** Elements that carry geometry. `<line>` has no fill, so it renders nothing here. */
const SHAPE_ELEMENTS = new Set(['path', 'rect', 'circle', 'ellipse', 'polygon', 'polyline']);

type ShapeGeometry =
  | { kind: 'rectangle'; geometry: RectangleObject['geometry']; ellipticalCorners?: boolean }
  | { kind: 'ellipse'; geometry: EllipseObject['geometry'] }
  | { kind: 'path'; geometry: PathObject['geometry'] };

function shapeGeometry(name: string, attributes: ReadonlyMap<string, string>, objectId: string): ShapeGeometry | null {
  if (name === 'rect') {
    const width = parseLength(attributes.get('width'), 'width');
    const height = parseLength(attributes.get('height'), 'height');
    // SVG disables rendering at zero rather than erroring, and a negative is an
    // error there too — so this matches, instead of inventing a shape.
    if (width <= 0 || height <= 0) return null;
    const hasRx = attributes.has('rx');
    const hasRy = attributes.has('ry');
    // Either radius alone stands for both, per the spec.
    const rx = Math.min(width / 2, Math.max(0, parseLength(attributes.get('rx'), 'rx', hasRy ? parseLength(attributes.get('ry'), 'ry') : 0)));
    const ry = Math.min(height / 2, Math.max(0, parseLength(attributes.get('ry'), 'ry', hasRx ? rx : 0)));
    // Maipo's corner radius is a single value per corner, so an elliptical
    // corner is approximated by its smaller axis rather than silently squared.
    const radius = Math.min(rx, ry);
    return {
      kind: 'rectangle',
      ...(Math.abs(rx - ry) > 0.001 ? { ellipticalCorners: true } : {}),
      geometry: {
        x: parseLength(attributes.get('x'), 'x'),
        y: parseLength(attributes.get('y'), 'y'),
        width,
        height,
        radius: { topLeft: radius, topRight: radius, bottomRight: radius, bottomLeft: radius },
      },
    };
  }
  if (name === 'circle' || name === 'ellipse') {
    const rx = name === 'circle' ? parseLength(attributes.get('r'), 'r') : parseLength(attributes.get('rx'), 'rx');
    const ry = name === 'circle' ? rx : parseLength(attributes.get('ry'), 'ry');
    if (rx <= 0 || ry <= 0) return null;
    return {
      kind: 'ellipse',
      geometry: {
        x: parseLength(attributes.get('cx'), 'cx') - rx,
        y: parseLength(attributes.get('cy'), 'cy') - ry,
        width: rx * 2,
        height: ry * 2,
      },
    };
  }
  const commands = name === 'path'
    ? parsePathData(attributes.get('d') ?? '')
    : parsePoints(attributes.get('points') ?? '', name === 'polygon');
  if (commands.length === 0) return null;
  const geometry = migratePathCommands(commands, objectId);
  return geometry.nodes.length === 0 ? null : { kind: 'path', geometry };
}

export interface SvgImport {
  readonly document: EditorDocument;
  readonly objects: number;
  readonly swatches: number;
  readonly notices: readonly ImportNotice[];
}

export function parseSvgDocument(text: string, name: string, signal?: AbortSignal): SvgImport {
  let width = 0;
  let height = 0;
  let root: Matrix = IDENTITY_MATRIX;
  let sawRoot = false;
  const stack: InheritedState[] = [];
  const objects: VectorObject[] = [];
  const children: Layer[] = [];
  const swatches: GlobalSwatch[] = [];
  // First-appearance order, keyed on the canonical hex, so two paths written
  // `#EE4646` and `#ee4646` share one swatch rather than looking identical and
  // recolouring separately.
  const swatchOfColour = new Map<string, GlobalSwatch>();
  const countOfColour = new Map<string, number>();
  const notices: ImportNotice[] = [];
  const definitions = collectDefinitions(text, signal);
  let definitionDepth = 0;
  let insideClip = false;
  const approximatedGradients = new Set<string>();
  let ellipticalCorners = 0;

  const onElement = (element: SvgElement) => {
    throwIfAborted(signal);
    const parent: InheritedState = stack[stack.length - 1] ?? { transform: IDENTITY_MATRIX, children, fill: '#000000', fillAlpha: 1, stroke: 'none', fillOpacity: 1, fillRule: 'nonzero', opacity: 1, display: 'inline', displayed: true, visibility: 'visible', clips: [], gradientFill: undefined };
    const attributes = inlinePaintAttributes(element.attributes);
    // Clip definitions use a separate rendering context. Do not apply ordinary
    // artwork visibility rules to the existing bounded clip approximation.
    if ((element.name === 'clippath' || insideClip) && (attributes.has('display') || attributes.has('visibility'))) {
      throw new SvgParseError(CLIP_VISIBILITY_ERROR);
    }
    if (element.name === 'defs') { definitionDepth += 1; return; }
    // Definitions were gathered up front; here their contents are only skipped.
    if (element.name === 'clippath') { if (!element.selfClosing) insideClip = true; return; }
    if (element.name === 'lineargradient' || element.name === 'radialgradient' || element.name === 'stop') return;

    if (element.name === 'svg') {
      if (sawRoot) throw new SvgParseError('This SVG nests another <svg>, which this reader does not support.');
      sawRoot = true;
      const viewBox = attributes.get('viewbox');
      const boxParts = viewBox?.split(/[\s,]+/).filter(Boolean).map(Number);
      width = dimension(attributes.get('width'), boxParts?.[2] ?? 0);
      height = dimension(attributes.get('height'), boxParts?.[3] ?? 0);
      root = viewportMatrix(viewBox, width, height, attributes.get('preserveaspectratio'));
    }

    const local = attributes.get('transform');
    const localTransform = local ? parseTransformList(local) : IDENTITY_MATRIX;
    // SVG 2 treats a root transform as an outer parent of the viewport mapping.
    // Descendant transforms continue to compose inside their parent mapping.
    const transform = element.name === 'svg'
      ? multiplyMatrix(localTransform, root)
      : multiplyMatrix(parent.transform, localTransform);
    const declaredFill = attributes.get('fill')?.trim();
    const fillKeyword = declaredFill?.toLowerCase();
    const fillAttribute = declaredFill === undefined || fillKeyword === 'inherit' || fillKeyword === 'unset'
      ? undefined : fillKeyword === 'initial' ? '#000000' : declaredFill;
    const isGradientFill = fillAttribute !== undefined && /^url\(#[^)]+\)$/i.test(fillAttribute);
    const paint = fillAttribute === undefined ? { colour: parent.fill, alpha: parent.fillAlpha }
      : isGradientFill ? { colour: null, alpha: 1 } : parseColourAlpha(fillAttribute);
    const fill = paint.colour;
    // A solid/none/initial declaration replaces the entire inherited paint,
    // including its gradient reference, before descendants inherit it.
    const inheritedGradient = fillAttribute === undefined ? parent.gradientFill : isGradientFill ? fillAttribute : undefined;
    const fillOpacity = paintOpacity(attributes.get('fill-opacity'), parent.fillOpacity, true);
    const opacity = paintOpacity(attributes.get('opacity'), parent.opacity, false);
    const rule = attributes.get('fill-rule')?.trim().toLowerCase();
    if (rule !== undefined && !['nonzero', 'evenodd', 'inherit', 'initial', 'unset'].includes(rule)) throw new SvgParseError('This SVG has an unsupported fill-rule value.');
    const fillRule = rule === 'initial' ? 'nonzero' : rule === 'nonzero' || rule === 'evenodd' ? rule : parent.fillRule;
    const strokeAttribute = attributes.get('stroke')?.trim().toLowerCase();
    const stroke = strokeAttribute === undefined || strokeAttribute === 'inherit' || strokeAttribute === 'unset'
      ? parent.stroke : strokeAttribute === 'initial' ? 'none' : strokeAttribute;
    const displayAttribute = attributes.get('display')?.trim().toLowerCase();
    const display = displayAttribute === 'inherit' ? parent.display
      : displayAttribute === undefined || displayAttribute === 'initial' || displayAttribute === 'unset' ? 'inline' : displayAttribute;
    if (!['none', 'inline', 'block', 'inline-block'].includes(display)) throw new SvgParseError('This SVG has an unsupported display value.');
    const visibilityAttribute = attributes.get('visibility')?.trim().toLowerCase();
    const visibility = visibilityAttribute === undefined || visibilityAttribute === 'inherit' || visibilityAttribute === 'unset'
      ? parent.visibility : visibilityAttribute === 'initial' ? 'visible' : visibilityAttribute;
    if (visibility !== 'visible' && visibility !== 'hidden' && visibility !== 'collapse') throw new SvgParseError('This SVG has an unsupported visibility value.');
    let targetChildren = parent.children;
    if ((element.name === 'g' || element.name === 'svg') && opacity < 1 && !insideClip && definitionDepth === 0) {
      const group = createGroupLayer(attributes.get('id') ?? (element.name === 'svg' ? name : 'SVG group'));
      group.opacity = opacity;
      // Geometry already carries the SVG transforms; this group only composites.
      parent.children.push(group);
      targetChildren = group.children;
    }
    const state: InheritedState = {
      transform,
      children: targetChildren,
      display,
      displayed: parent.displayed && display !== 'none',
      visibility,
      fill,
      fillAlpha: paint.alpha,
      stroke,
      fillRule,
      fillOpacity,
      opacity,
      clips: (() => {
        const id = clipPathReference(attributes.get('clip-path'));
        return id === null ? parent.clips : [...parent.clips, { id, transform }];
      })(),
      gradientFill: inheritedGradient,
    };
    // scanSvg emits onClose for self-closing elements too. Balance every shape
    // so one sibling cannot pop the inherited state of its parent group.
    stack.push(state);

    // Inside a clipPath, shapes define the clip; they are never drawn.
    if (insideClip) return;
    // Anything else inside <defs> is a definition this reader does not use.
    if (definitionDepth > 0) return;
    if ((SHAPE_ELEMENTS.has(element.name) || element.name === 'line') && state.stroke !== 'none') {
      throw new SvgParseError(`This SVG has a stroked <${element.name}>, which this reader does not yet support. Outline the strokes and try again.`);
    }

    // Lines have no filled area, but must pass the stroke check before skipping.
    if (!SHAPE_ELEMENTS.has(element.name)) return;

    const objectId = createId('vector');
    // Basic shapes become Maipo's NATIVE rectangle and ellipse objects rather
    // than four-node paths. Converting everything to paths would open the file
    // just as well and lose what makes it worth opening: a native rectangle
    // keeps width/height fields and corner-radius handles, and a native ellipse
    // stays an ellipse instead of four cubics nobody can round-trip.
    const shape = shapeGeometry(element.name, attributes, objectId);
    if (!shape) return;

    // SVG's initial fill is black, and a path with no fill attribute anywhere up
    // the tree is genuinely black rather than invisible.
    let colour = state.fill ?? null;
    // A gradient becomes its first stop, and says so. Refusing an entire
    // illustration because two of its seven hundred paths carry a gradient
    // would be technically defensible and useless; a named approximation in the
    // import report is the trade the format's notice mechanism exists for.
    const rawFill = state.gradientFill;
    const gradientId = rawFill ? /^url\(#([^)]+)\)$/i.exec(rawFill.trim())?.[1] : undefined;
    if (gradientId) {
      const stops = gradientStops(definitions, gradientId);
      colour = stops[0] ?? null;
      if (!approximatedGradients.has(gradientId)) {
        approximatedGradients.add(gradientId);
        notices.push(colour ? {
          severity: 'warning',
          feature: 'Gradient fill',
          message: `The gradient “${gradientId}” became its first colour ${colour}. Maipo fills are solid.`,
          handling: 'approximated',
        } : {
          // SVG paints nothing for a missing or stop-less gradient; so does this.
          severity: 'warning',
          feature: 'Gradient fill',
          message: `The gradient “${gradientId}” is missing or has no colour stops, so SVG paints nothing with it; shapes using it were omitted.`,
          handling: 'omitted',
        });
      }
    }
    if (colour === null) return;

    // Clipping. A clip made only of rectangles that already contains the shape
    // changes nothing, and generated files use exactly that — a full-canvas rect
    // wrapping everything. Anything else would alter the artwork, so it is
    // refused rather than silently ignored.
    for (const reference of state.clips) {
      const clip = definitions.clips.get(reference.id);
      if (!clip || !clip.exact || clip.objectBoundingBox || !clipContains(clip, reference, shape, state.transform)) {
        throw new SvgParseError(`This SVG clips artwork with “${reference.id}”, and this reader only accepts a clip that changes nothing. Flatten the clip and try again.`);
      }
    }
    if (shape.kind === 'rectangle' && shape.ellipticalCorners) ellipticalCorners += 1;
    let swatch = swatchOfColour.get(colour);
    if (!swatch) {
      swatch = createGlobalSwatch(colour);
      swatchOfColour.set(colour, swatch);
      swatches.push(swatch);
    }
    const index = (countOfColour.get(colour) ?? 0) + 1;
    countOfColour.set(colour, index);
    const base = {
      id: objectId,
      // Named exactly as a trace names its regions, so imported and traced
      // artwork read the same in the layer list.
      name: `${colour.toUpperCase()} ${index}`,
      visible: state.displayed && state.visibility === 'visible',
      opacity: state.opacity,
      transform: state.transform,
      fill: { type: 'solid' as const, color: colour, alpha: state.fillOpacity * state.fillAlpha, swatchId: swatch.id },
      stroke: null,
      extensions: {},
    };
    if (shape.kind === 'rectangle') objects.push({ ...base, type: 'rectangle', geometry: shape.geometry });
    else if (shape.kind === 'ellipse') objects.push({ ...base, type: 'ellipse', geometry: shape.geometry });
    else objects.push({ ...base, type: 'path', geometry: { ...shape.geometry, fillRule } });
    const object = objects[objects.length - 1]!;
    const last = state.children[state.children.length - 1];
    if (last?.kind === 'vector') last.vector.objects.push(object);
    else state.children.push({ ...createLayerBase(name), kind: 'vector',
      vector: { schemaVersion: 2, objects: [object], extensions: {} } });
  };

  scanSvg(text, onElement, (name) => {
    if (name === 'defs') { definitionDepth = Math.max(0, definitionDepth - 1); return; }
    if (name === 'clippath') { insideClip = false; return; }
    if (name === 'lineargradient' || name === 'radialgradient' || name === 'stop') return;
    stack.pop();
  });
  if (ellipticalCorners > 0) {
    notices.push({
      severity: 'warning',
      feature: 'Elliptical corners',
      message: `${ellipticalCorners.toLocaleString()} rectangle${ellipticalCorners === 1 ? ' has' : 's have'} elliptical corners (rx ≠ ry). Maipo corners are circular, so the smaller radius was used.`,
      handling: 'approximated',
    });
  }

  if (!sawRoot) throw new SvgParseError('This file has no <svg> root element.');
  if (width <= 0 || height <= 0) throw new SvgParseError('This SVG declares no usable width and height.');
  if (objects.length === 0) throw new SvgParseError('This SVG contains no filled shapes to import.');

  const document = createDocument({ name, width, height, background: 'transparent' });
  // SVG paints in source order; layer arrays store the topmost item first.
  // Vector objects themselves stay in source paint order within each batch.
  const layerOrder = (layers: Layer[]) => {
    layers.reverse();
    for (const layer of layers) if (layer.kind === 'group') layerOrder(layer.children);
  };
  layerOrder(children);
  document.root.children = children;
  document.extensions[GLOBAL_SWATCHES_EXTENSION] = swatches;
  document.activeLayerIds = [document.root.children[0]!.id];
  return { document, objects: objects.length, swatches: swatches.length, notices };
}

export class SvgCodec implements DocumentCodec {
  readonly id = 'svg';
  readonly label = 'SVG';

  canRead(file: File): boolean {
    return /\.svg$/i.test(file.name) || file.type === 'image/svg+xml';
  }

  async read(file: File, context?: CodecContext): Promise<CodecReadResult> {
    throwIfAborted(context?.signal);
    if (file.size > SVG_LIMITS.maxSourceBytes) {
      throw new SvgParseError(`This SVG is larger than ${(SVG_LIMITS.maxSourceBytes / 1024 / 1024).toFixed(0)}MB.`);
    }
    context?.onProgress?.({ phase: 'validating', progress: 0, message: 'Reading SVG' });
    const bytes = new Uint8Array(await file.arrayBuffer());
    // Fatal decoding: a file that is not valid UTF-8 is not a file this reads.
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    context?.onProgress?.({ phase: 'converting', progress: 0.5, message: 'Converting paths' });
    const imported = parseSvgDocument(text, file.name.replace(/\.svg$/i, ''), context?.signal);
    context?.onProgress?.({ phase: 'converting', progress: 1, message: 'Imported' });
    return {
      document: imported.document,
      report: {
        codec: this.id,
        sourceName: file.name,
        notices: [
          {
            severity: 'info' as const,
            feature: 'Shared colours',
            message: `${imported.objects.toLocaleString()} shapes sharing ${imported.swatches} colours. Editing a swatch recolours every shape using it.`,
            handling: 'preserved' as const,
          },
          ...imported.notices,
        ],
      },
    };
  }
}
